/**
 * Runtime brand config: the network half.
 *
 * `refreshBrandConfig()` is fired once at app start, next to `Geo.resolve()`.
 * It is FAIL-SAFE BY CONSTRUCTION:
 *
 *   - it is never awaited by anything that gates rendering, so a slow endpoint
 *     cannot delay, hang or blank the app;
 *   - it never rejects and never throws — every failure path is swallowed and
 *     logged at debug level;
 *   - it is a hard no-op for brands with no `brandConfigUrl`
 *     (cooey / eagledrive / yeet), which never open a socket;
 *   - the request is abandoned after {@link BRAND_CONFIG_TIMEOUT_MS};
 *   - the response is validated before it is allowed anywhere near the app.
 *
 * A dead admin host therefore costs one aborted request and nothing else: the
 * session keeps whatever `#/lib/brand` resolved at import time (the cached
 * config, else the compiled brand).
 *
 * WHAT IT AFFECTS: the validated document is written to the device cache, and
 * `#/lib/brand` applies it at the NEXT cold start. It deliberately does not
 * mutate the live brand mid-session — see the note in `#/lib/brand/index`.
 */

import {COMPILED_BRAND} from '#/lib/brand'
import {
  clearCachedBrandConfig,
  readCachedBrandConfig,
  writeCachedBrandConfig,
} from '#/lib/brand/cache'
import {validateBrandConfig} from '#/lib/brand/runtime'
import {Logger} from '#/logger'

const logger = Logger.create(Logger.Context.Brand)

/**
 * Deliberately short. This is a cosmetic config document; anything slower than
 * this is not worth waiting on and the cached/compiled brand is already live.
 */
export const BRAND_CONFIG_TIMEOUT_MS = 3_000

/** Refuse absurd payloads before parsing them. */
const MAX_BYTES = 128 * 1024

let inflight: Promise<void> | undefined

/**
 * Fetch + cache this brand's runtime config. Safe to call more than once (the
 * first call's promise is reused). Resolves — never rejects — always.
 */
export function refreshBrandConfig(): Promise<void> {
  if (!inflight) {
    inflight = run().catch(() => {})
  }
  return inflight
}

async function run(): Promise<void> {
  const url = COMPILED_BRAND.brandConfigUrl
  // Brands with no runtime config behave exactly as before: no fetch, no
  // storage access, nothing.
  if (!url) return
  if (!/^https:\/\//i.test(url) && !/^http:\/\/localhost[:/]/i.test(url)) {
    logger.warn('brand config: refusing non-https brandConfigUrl')
    return
  }

  let body: unknown
  try {
    body = await fetchWithTimeout(url)
  } catch (e) {
    // Down, unreachable, 404, CORS, timeout, non-JSON — all the same to us.
    logger.debug('brand config: fetch failed, keeping current brand', {
      safeMessage: e instanceof Error ? e.message : 'unknown error',
    })
    return
  }

  const config = validateBrandConfig(body, COMPILED_BRAND.id)
  if (!config) {
    /*
     * The endpoint answered with something we cannot trust (wrong brand,
     * malformed shape). Keep the last known-good cache rather than replacing
     * it with junk — but if there is no cache, there is nothing to do either.
     */
    logger.warn('brand config: document failed validation, ignoring')
    return
  }

  const previous = readCachedBrandConfig(COMPILED_BRAND.id)
  if (JSON.stringify(previous) === JSON.stringify(config)) return

  writeCachedBrandConfig(config)
  logger.debug('brand config: cached a new document', {
    updatedAt: config.updatedAt,
  })
}

async function fetchWithTimeout(url: string): Promise<unknown> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), BRAND_CONFIG_TIMEOUT_MS)
  try {
    /*
     * No custom headers: that keeps this a CORS "simple request" so it never
     * costs a preflight, and lets pds-pro's own `Cache-Control`/`ETag` govern
     * revalidation.
     */
    const res = await fetch(url, {signal: controller.signal})
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const length = Number(res.headers.get('content-length') ?? 0)
    if (length > MAX_BYTES) throw new Error('document too large')
    const raw = await res.text()
    if (raw.length > MAX_BYTES) throw new Error('document too large')
    return JSON.parse(raw)
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Drop the cached document. Exposed for debugging/QA ("show me the compiled
 * brand again") — the next refresh re-populates it.
 */
export function resetBrandConfig(): void {
  clearCachedBrandConfig()
}
